// Persists Otto's conversation so the chat window survives page navigation AND a full browser refresh.
// The dashboard mounts FilterAssistant inside a route, so navigating away unmounts it and a reload wipes
// React state entirely — both used to throw the conversation away. We snapshot the durable chat state
// (the messages, the server session handle, and whether the panel is open) into localStorage and rehydrate
// it on mount. Only the Clear button empties this; navigation and refresh keep it.
//
// localStorage (not sessionStorage) is deliberate: it survives a full reload AND a new tab, matching the
// "keep my chat until I clear it" expectation. Transient state (the in-flight `pendingText` and the draft
// input) is intentionally NOT persisted — replaying a stale queued query after a reload would be surprising.

// Legacy unscoped key. Reads fall back to it once (a chat persisted before per-user scoping); writes go
// to the scoped key only, so the legacy copy just goes stale.
const KEY = "otto.chat.v1";

// Keep only the newest messages. An unbounded chat eventually hits the localStorage quota — at which point
// the WHOLE save silently stops persisting — and every kept message pays its rendering cost on each mount.
export const MAX_PERSISTED_MESSAGES = 40;

// Access localStorage defensively: it can be absent (SSR/tests) or throw on read (some incognito/embedded
// contexts). Any failure degrades to "no persistence" rather than breaking the dashboard.
const storage = () => {
    try {
        return typeof window !== "undefined" ? window.localStorage : null;
    } catch (e) {
        return null;
    }
};

// The logged-in user's id (persisted by the app in localStorage). The chat record is keyed by it so two
// users on one machine can't restore each other's conversation. Shared with the widget, which scopes its
// server calls (interpret, config, the history list) to the same id. Read defensively, like `storage`.
export const readUserId = () => {
    try {
        return typeof window !== "undefined" && window.localStorage ? window.localStorage.getItem("userId") : null;
    } catch (e) {
        return null;
    }
};

const scopedKey = () => `${KEY}.${readUserId() || "anon"}`;

// Strip EXECUTION semantics from a message headed for (or read back from) storage. An autoApply table card
// or autoRun plan step applies itself once, on the mount that CREATED it; a restored copy re-executing on
// every remount replays sorts/hides/filters — including ones the user explicitly undid — on every page it
// follows them to. A restored card renders its manual Apply button instead (the designed non-auto card).
// Applied on load too, so a record written by an older build is just as inert. Returns new objects — the
// live in-memory messages are never touched.
const neutralizeMessage = m => {
    if (!m || typeof m !== "object") return m;
    const { autoRun, ...rest } = m;
    // An in-flight PROGRESS bubble (an export being built) is live UI owned by the mount that started it.
    // Restored, its spinner would turn forever for an export that finished — or died with the page — long
    // ago, so it freezes into a static line instead. Written frozen too: a record saved mid-export is stale
    // the moment it's read back.
    if (rest.progress && rest.progress.status === "running") {
        return { ...rest, progress: { ...rest.progress, status: "stale" } };
    }
    return rest.proposal && rest.proposal.autoApply ? { ...rest, proposal: { ...rest.proposal, autoApply: false } } : rest;
};

// A persisted panel size is only usable if both axes are finite positive numbers — anything else (a
// hand-edited record, a half-written blob) falls back to the default size rather than rendering a 0px or
// NaN-wide window.
const readSize = s =>
    s && Number.isFinite(s.w) && Number.isFinite(s.h) && s.w > 0 && s.h > 0 ? { w: s.w, h: s.h } : null;

export const loadChat = () => {
    const s = storage();
    if (!s) return null;
    try {
        // Prefer the per-user record; fall back to the legacy unscoped key for a chat saved before scoping
        // existed. The next save writes the scoped key, which shadows the legacy copy from then on.
        const raw = s.getItem(scopedKey()) || s.getItem(KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || !Array.isArray(data.messages)) return null;
        return {
            messages: data.messages.map(neutralizeMessage),
            sessionId: data.sessionId || null,
            open: !!data.open,
            // Indices of the cards whose filters are live on the dashboard, in apply order (the accumulate
            // stack). Persisted so the applied/Undo state survives navigation + reload — without it a
            // reopened chat wrongly shows "Apply" on already-applied cards. `appliedFilterIdx` is the legacy
            // single-index field, returned too so the widget can migrate an older persisted chat once.
            appliedStack: Array.isArray(data.appliedStack) ? data.appliedStack.filter(n => typeof n === "number") : undefined,
            appliedFilterIdx: typeof data.appliedFilterIdx === "number" ? data.appliedFilterIdx : null,
            // The window size the user resized/enlarged Otto to. Persisted because the widget remounts on
            // every page navigation — without this an enlarged window would snap back to default each time.
            // The caller re-clamps to the current viewport, so a size saved on a bigger screen is safe.
            panelSize: readSize(data.panelSize)
        };
    } catch (e) {
        return null; // corrupt or unreadable — start fresh rather than crash on rehydrate
    }
};

export const saveChat = ({ messages, sessionId, open, appliedStack, panelSize }) => {
    const s = storage();
    if (!s) return;
    try {
        const all = messages || [];
        const dropped = Math.max(0, all.length - MAX_PERSISTED_MESSAGES);
        s.setItem(
            scopedKey(),
            JSON.stringify({
                messages: all.slice(dropped).map(neutralizeMessage),
                sessionId: sessionId || null,
                open: !!open,
                // appliedStack holds INDICES into `messages`, so dropping the head shifts every survivor
                // down by `dropped`; an index whose message fell off the cap goes with it. Apply order
                // (what drives newest-first Undo) is preserved.
                appliedStack: (Array.isArray(appliedStack) ? appliedStack.filter(n => typeof n === "number") : [])
                    .map(n => n - dropped)
                    .filter(n => n >= 0),
                panelSize: readSize(panelSize)
            })
        );
    } catch (e) {
        /* quota exceeded / blocked — persistence is best-effort, never break the chat over it */
    }
};
